import { useState, useEffect, useMemo } from "react";
import { Link } from "react-router-dom";
import { api } from "@/services/api";
import { VILLA_MODELS } from "@/pages/home/villaModels";

interface Stats {
  users: number;
  properties: number;
  leads: number;
  deals: number;
  pipeline_value: number;
  revenue: number;
  pending_reminders: number;
  media: number;
}

interface LeadTrendPoint { date: string; total: number }
interface TxTrendPoint { month: string; count: number; amount: number }
interface RecentLead { id: number; full_name: string; phone: string; source: string; status: string; created_at: string; rm_name: string }
interface RecentProperty { id: number; title: string; status: string; price: string; city: string; primary_image: string }

interface DashboardData {
  stats: Stats;
  lead_trend: LeadTrendPoint[];
  transaction_trend: TxTrendPoint[];
  properties_by_status: { status: string; total: number }[];
  leads_by_source: { source: string; total: number }[];
  leads_by_villa: { page: string; total: number }[];
  recent_leads: RecentLead[];
  recent_properties: RecentProperty[];
}

const STATUS_BADGE: Record<string, string> = {
  new: "bg-secondary-100 text-secondary-900",
  contacted: "bg-primary-100 text-primary-900",
  qualified: "bg-accent-100 text-accent-900",
  proposal: "bg-accent-100 text-accent-900",
  won: "bg-primary-600 text-background-50",
  lost: "bg-background-200 text-foreground-700",
};

function villaNameFromPage(page: string): string {
  if (!page || !page.startsWith("villa-")) return "";
  const slug = page.slice("villa-".length);
  return VILLA_MODELS.find((v) => v.slug === slug)?.name || "";
}

export default function AdminDashboard() {
  const [data, setData] = useState<DashboardData | null>(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let active = true;
    api
      .get<DashboardData>("/dashboard/overview")
      .then((d) => {
        if (active) setData(d);
      })
      .catch((e) => {
        if (active) setError(e?.message || "Failed to load dashboard");
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, []);

  const cards = useMemo(() => {
    if (!data) return [];
    const s = data.stats;
    return [
      { label: "Projects", value: s.properties, icon: "ri-building-2-line", tint: "bg-primary-100 text-primary-800", to: "/admin/properties" },
      { label: "Active Leads", value: s.leads, icon: "ri-contacts-book-line", tint: "bg-accent-100 text-accent-800", to: "/admin/leads" },
      { label: "Open Deals", value: s.deals, icon: "ri-hand-coin-line", tint: "bg-secondary-100 text-secondary-900", to: "/admin/leads" },
      { label: "Pipeline Value", value: `₹${Number(s.pipeline_value || 0).toLocaleString("en-IN")}`, icon: "ri-vip-diamond-line", tint: "bg-primary-100 text-primary-800", to: "/admin/reports" },
      { label: "Revenue", value: `₹${Number(s.revenue || 0).toLocaleString("en-IN")}`, icon: "ri-exchange-funds-line", tint: "bg-accent-100 text-accent-800", to: "/admin/transactions" },
      { label: "Team Users", value: s.users, icon: "ri-team-line", tint: "bg-secondary-100 text-secondary-900", to: "/admin/users" },
    ];
  }, [data]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-24">
        <i className="ri-loader-4-line animate-spin text-2xl text-primary-600"></i>
      </div>
    );
  }

  if (error) {
    return (
      <div className="rounded-3xl bg-accent-100 border border-accent-300 p-8 text-center max-w-xl mx-auto">
        <i className="ri-error-warning-line text-3xl text-accent-800"></i>
        <p className="mt-3 text-sm text-accent-900">{error}</p>
        <p className="text-xs text-accent-800 mt-2">
          Make sure the Node backend is running and reachable at /api.
        </p>
        <button
          onClick={() => window.location.reload()}
          className="mt-4 inline-flex items-center gap-2 text-sm font-semibold whitespace-nowrap rounded-full bg-primary-600 text-background-50 px-5 py-2.5 hover:bg-primary-700"
        >
          <i className="ri-refresh-line"></i> Retry
        </button>
      </div>
    );
  }

  if (!data) return null;

  const maxLead = Math.max(...data.lead_trend.map((p) => p.total), 1);

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4">
        {cards.map((card) => (
          <Link
            key={card.label}
            to={card.to}
            className="flex items-center gap-4 rounded-2xl bg-background-50 border border-background-200 p-5 hover:border-primary-300 transition-colors"
          >
            <span className={`flex items-center justify-center w-12 h-12 rounded-xl ${card.tint}`}>
              <i className={`${card.icon} text-xl`}></i>
            </span>
            <div className="min-w-0">
              <p className="text-xs text-foreground-600">{card.label}</p>
              <p className="text-xl md:text-2xl font-bold text-foreground-950 truncate">
                {card.value}
              </p>
            </div>
          </Link>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Lead trend */}
        <div className="lg:col-span-2 rounded-2xl bg-background-50 border border-background-200 p-5">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-heading text-base text-foreground-950">Leads (last 14 days)</h3>
            <Link to="/admin/leads" className="text-xs font-semibold text-primary-700 hover:text-primary-800">
              View all
            </Link>
          </div>
          <div className="flex items-end gap-1.5 h-36">
            {data.lead_trend.map((p) => (
              <div key={p.date} className="flex-1 flex flex-col items-center gap-1">
                <div className="w-full flex items-end justify-center" style={{ height: "100%" }}>
                  <div
                    className="w-full max-w-8 rounded-t bg-primary-500 hover:bg-primary-600 transition-colors"
                    style={{ height: `${Math.max((p.total / maxLead) * 100, 4)}%` }}
                    title={`${p.date}: ${p.total}`}
                  ></div>
                </div>
                <span className="text-[9px] text-foreground-500">
                  {new Date(p.date + "T00:00:00").toLocaleDateString("en-IN", { day: "2-digit", month: "short" })}
                </span>
              </div>
            ))}
          </div>
        </div>

        {/* Leads by source */}
        <div className="rounded-2xl bg-background-50 border border-background-200 p-5">
          <h3 className="font-heading text-base text-foreground-950 mb-4">Leads by Source</h3>
          <div className="space-y-3">
            {data.leads_by_source.map((s) => (
              <div key={s.source}>
                <div className="flex items-center justify-between text-sm mb-1">
                  <span className="text-foreground-700 capitalize">{s.source}</span>
                  <span className="font-semibold text-foreground-950">{s.total}</span>
                </div>
                <div className="h-2 rounded-full bg-background-100 overflow-hidden">
                  <div
                    className="h-full rounded-full bg-accent-500"
                    style={{ width: `${(s.total / Math.max(data.leads_by_source[0]?.total || 1, 1)) * 100}%` }}
                  ></div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="rounded-2xl bg-background-50 border border-background-200 p-5">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-heading text-base text-foreground-950">Leads by Villa Model</h3>
          <Link to="/admin/leads" className="text-xs font-semibold text-primary-700 hover:text-primary-800">
            View all
          </Link>
        </div>
        {data.leads_by_villa.length === 0 ? (
          <p className="py-8 text-center text-sm text-foreground-600">No villa leads yet.</p>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {data.leads_by_villa.map((v) => {
              const max = Math.max(...data.leads_by_villa.map((x) => x.total), 1);
              const name = villaNameFromPage(v.page) || v.page.replace("villa-", "");
              return (
                <div key={v.page} className="rounded-2xl bg-background-100 border border-background-200 p-4">
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-semibold text-foreground-950">{name}</span>
                    <span className="inline-flex items-center gap-1 text-xs font-bold text-accent-700 bg-accent-100 rounded-full px-2.5 py-1">
                      <i className="ri-vip-diamond-line"></i> {v.total}
                    </span>
                  </div>
                  <div className="mt-3 h-2 rounded-full bg-background-200 overflow-hidden">
                    <div className="h-full rounded-full bg-accent-500" style={{ width: `${(v.total / max) * 100}%` }}></div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Recent leads */}
        <div className="rounded-2xl bg-background-50 border border-background-200 overflow-hidden">
          <div className="flex items-center justify-between px-5 py-4 border-b border-background-100">
            <h3 className="font-heading text-base text-foreground-950">Recent Leads</h3>
            <Link to="/admin/leads" className="text-xs font-semibold text-primary-700 hover:text-primary-800">
              View all
            </Link>
          </div>
          <div className="divide-y divide-background-100">
            {data.recent_leads.map((lead) => (
              <div key={lead.id} className="flex items-center justify-between gap-3 px-5 py-3">
                <div className="min-w-0">
                  <p className="text-sm font-semibold text-foreground-950 truncate">{lead.full_name}</p>
                  <p className="text-xs text-foreground-600">{lead.phone || lead.email || "—"}</p>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <span className={`px-2 py-1 rounded-full text-[10px] font-semibold capitalize ${STATUS_BADGE[lead.status] || "bg-background-200 text-foreground-700"}`}>
                    {lead.status}
                  </span>
                  <span className="text-[11px] text-foreground-500">
                    {new Date(lead.created_at + "Z").toLocaleDateString("en-IN")}
                  </span>
                </div>
              </div>
            ))}
            {data.recent_leads.length === 0 && (
              <p className="px-5 py-8 text-center text-sm text-foreground-600">No leads yet.</p>
            )}
          </div>
        </div>

        {/* Recent properties */}
        <div className="rounded-2xl bg-background-50 border border-background-200 overflow-hidden">
          <div className="flex items-center justify-between px-5 py-4 border-b border-background-100">
            <h3 className="font-heading text-base text-foreground-950">Recent Projects</h3>
            <Link to="/admin/properties" className="text-xs font-semibold text-primary-700 hover:text-primary-800">
              View all
            </Link>
          </div>
          <div className="divide-y divide-background-100">
            {data.recent_properties.map((p) => (
              <div key={p.id} className="flex items-center gap-3 px-5 py-3">
                <span className="flex items-center justify-center w-10 h-10 rounded-lg bg-primary-100 text-primary-800 shrink-0">
                  <i className="ri-building-2-line"></i>
                </span>
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-semibold text-foreground-950 truncate">{p.title}</p>
                  <p className="text-xs text-foreground-600">{p.city || "—"}</p>
                </div>
                <div className="text-right shrink-0">
                  <p className="text-sm font-bold text-foreground-950">
                    ₹{Number(p.price || 0).toLocaleString("en-IN")}
                  </p>
                  <p className="text-[11px] text-foreground-500 capitalize">{p.status}</p>
                </div>
              </div>
            ))}
            {data.recent_properties.length === 0 && (
              <p className="px-5 py-8 text-center text-sm text-foreground-600">No projects yet.</p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}